import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { queryKeys } from '@/api/query-keys';
import { useAuth } from '@/features/auth/auth-provider';
import { PERMISSIONS } from '@/features/auth/permissions';

import { createLocale, deleteLocale, fetchLocales, updateLocale } from './api';
import type { LocaleFormValues } from './types';

export function useLocales() {
  const { can } = useAuth();
  const allowed = can(PERMISSIONS.locales.view);

  return useQuery({
    queryKey: queryKeys.locales.list(),
    queryFn: fetchLocales,
    enabled: allowed,
    staleTime: 60_000,
  });
}

export function useCreateLocale() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (values: LocaleFormValues) => createLocale(values),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.locales.all() });
    },
  });
}

export function useUpdateLocale() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, values }: { id: number; values: LocaleFormValues }) =>
      updateLocale(id, values),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.locales.all() });
    },
  });
}

export function useDeleteLocale() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => deleteLocale(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.locales.all() });
    },
  });
}
